import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CardsBorder from "../../../components/Cards/CardsBorder";
import CustomButton from "../../../components/Elements/Buttons/CustomButtons";
import PopupCard from "../../../components/Cards/PopupCard/PopupCard";
import { manageGroupLicense, fetchGroupById } from "../../../redux/group/groupSlice";

const GroupLicenseTable = () => {
  const dispatch = useDispatch();
  const { currentGroup } = useSelector((state) => state.group);
  const [count, setCount] = useState(0);
  const [popupData, setPopupData] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);
  const licenseData = currentGroup?.license?.group_view || {
    total: 0,
    used: 0,
    unused: 0,
  };

  const handleLicenseAction = async (action) => {
    if (count <= 0) {
      setPopupData({
        type: "error",
        title: "Invalid Count",
        message: "Please enter a count greater than 0",
      });
      setTimeout(() => setPopupData(null), 3000);
      return;
    }

    setLoadingAction(true);
    const payload = {
      count: parseInt(count),
      action: action,
    };

    try {
      const resultAction = await dispatch(
        manageGroupLicense({ id: currentGroup?._id, payload })
      );
      if (manageGroupLicense.fulfilled.match(resultAction)) {
        setPopupData({
          type: "success",
          title: "Success",
          message: `License ${action === "add" ? "added" : "removed"} successfully`,
        });
        dispatch(fetchGroupById(currentGroup?._id));
        setCount(0);
      } else {
        setPopupData({
          type: "error",
          title: "Failed",
          message: resultAction.payload || "Operation failed",
        });
      }
    } catch (error) {
      setPopupData({
        type: "error",
        title: "Error",
        message: "An unexpected error occurred",
      });
    } finally {
      setLoadingAction(false);
      setTimeout(() => setPopupData(null), 3000);
    }
  };

  return (
    <div style={{ paddingLeft: "40px", paddingRight: "40px", paddingTop: "20px" }}>
      <CardsBorder>
        <div className="p-8">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-[#06513D] text-2xl font-bold">Group License Management</h2>
            <div className="text-sm text-[#6B7280]">
              Group ID: <span className="font-semibold text-[#06513D]">{currentGroup?.groupId}</span>
            </div>
          </div>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            <div className="bg-gradient-to-br from-[#F0FDF4] to-[#DCFCE7] p-6 rounded-2xl border border-[#BBF7D0] shadow-sm transform transition-all hover:scale-[1.02]">
              <p className="text-[#166534] text-xs font-bold uppercase tracking-widest mb-2">Total Licenses</p>
              <div className="flex items-baseline gap-2">
                <span className="text-[#14532D] text-4xl font-extrabold">{licenseData.total}</span>
                <span className="text-[#166534] text-sm font-medium">available</span>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-[#FFF7ED] to-[#FFEDD5] p-6 rounded-2xl border border-[#FED7AA] shadow-sm transform transition-all hover:scale-[1.02]">
              <p className="text-[#9A3412] text-xs font-bold uppercase tracking-widest mb-2">Used Licenses</p>
              <div className="flex items-baseline gap-2">
                <span className="text-[#7C2D12] text-4xl font-extrabold">{licenseData.used}</span>
                <span className="text-[#9A3412] text-sm font-medium">allocated</span>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-[#F0F9FF] to-[#E0F2FE] p-6 rounded-2xl border border-[#BAE6FD] shadow-sm transform transition-all hover:scale-[1.02]">
              <p className="text-[#0369A1] text-xs font-bold uppercase tracking-widest mb-2">Unused Licenses</p>
              <div className="flex items-baseline gap-2">
                <span className="text-[#0C4A6E] text-4xl font-extrabold">{licenseData.unused}</span>
                <span className="text-[#0369A1] text-sm font-medium">remaining</span>
              </div>
            </div>
          </div>

          {/* Action Section */}
          <div className="bg-white p-8 border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] relative overflow-hidden">
             <div className="absolute top-0 left-0 w-1 h-full bg-[#06513D]"></div>
             <div className="flex flex-col md:flex-row items-end gap-6">
                <div className="flex-1 w-full">
                  <label className="block text-sm font-bold text-[#374151] mb-2 uppercase tracking-wide">
                    Add/Remove Licenses
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={count || ""}
                      onChange={(e) => setCount(e.target.value)}
                      placeholder="Enter license count"
                      className="w-full h-[50px] px-4 bg-[#F9FAFB] border border-[#D1D5DB] rounded-xl focus:ring-2 focus:ring-[#06513D] focus:border-transparent outline-none transition-all text-lg font-medium"
                      min="1"
                    />
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pr-4">
                      Units
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-4 w-full md:w-auto">
                  <CustomButton
                    buttonType="primary"
                    variant="ok"
                    buttonText={loadingAction ? "Processing..." : "Add License"}
                    onClick={() => handleLicenseAction("add")}
                    className="h-[50px] px-8 text-sm font-bold min-w-[150px] shadow-lg shadow-[#06513D]/20"
                    disabled={loadingAction}
                  />
                   <CustomButton
                    buttonType="primary"
                    variant="cancel"
                    buttonText={"Remove License"}
                    onClick={() => handleLicenseAction("remove")}
                    disabled={loadingAction}
                    className="h-[50px] px-8 text-sm font-bold min-w-[150px] border-2 border-red-500 text-red-500 rounded-xl hover:bg-red-50 transition-all active:scale-95 disabled:opacity-50"
                 />
                  
                </div>
             </div>
             <p className="mt-4 text-[12px] text-[#6B7280] italic">
               * Note: Licenses will be updated instantly across the group ecosystem.
             </p>
          </div>
        </div>
      </CardsBorder>

      {popupData && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] flex items-center justify-center transition-all duration-300">
          <PopupCard
            type={popupData.type}
            title={popupData.title}
            message={popupData.message}
            onClose={() => setPopupData(null)}
          />
        </div>
      )}
    </div>
  );
};

export default GroupLicenseTable;
